import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../../config/api';

interface TwilioConfiguration {
  id: number;
  user_id: number;
  name: string;
  account_sid: string;
  auth_token: string;
  sync_interval_hours: number;
  max_pages_per_run: number;
  page_size: number;
  email_to?: string;
  email_cc?: string;
  email_bcc?: string;
  is_active: boolean;
  auto_sync_enabled: boolean;
  last_run_messages?: string;
  last_run_calls?: string;
  last_auto_sync?: string;
  total_sms_count: number;
  total_calls_count: number;
  created_at: string;
  updated_at?: string;
}

interface TwilioConfigurationCreate {
  name: string;
  account_sid: string;
  auth_token: string;
  sync_interval_hours?: number;
  max_pages_per_run?: number;
  page_size?: number;
  email_to?: string;
  email_cc?: string;
  email_bcc?: string;
  is_active?: boolean;
  auto_sync_enabled?: boolean;
}

interface TwilioConfigurationUpdate {
  name?: string;
  account_sid?: string;
  auth_token?: string;
  sync_interval_hours?: number;
  max_pages_per_run?: number;
  page_size?: number;
  email_to?: string;
  email_cc?: string;
  email_bcc?: string;
  is_active?: boolean;
  auto_sync_enabled?: boolean;
}

interface TwilioMessage {
  id: number;
  configuration_id: number;
  user_id: number;
  sid: string;
  account_sid: string;
  date_created?: string;
  date_updated?: string;
  date_sent?: string;
  from_number?: string;
  to_number?: string;
  body?: string;
  num_segments?: number;
  num_media?: number;
  status?: string;
  direction?: string;
  error_code?: string;
  error_message?: string;
  price?: string;
  price_unit?: string;
  uri?: string;
  created_at: string;
}

interface TwilioCall {
  id: number;
  configuration_id: number;
  user_id: number;
  sid: string;
  account_sid: string;
  date_created?: string;
  date_updated?: string;
  start_time?: string;
  end_time?: string;
  from_number?: string;
  to_number?: string;
  duration?: number;
  status?: string;
  direction?: string;
  answered_by?: string;
  price?: string;
  price_unit?: string;
  uri?: string;
  created_at: string;
}

interface TwilioSyncRequest {
  force_full_sync?: boolean;
}

interface TwilioSyncResponse {
  success: boolean;
  new_messages: number;
  new_calls: number;
  total_messages: number;
  total_calls: number;
  message?: string;
}

interface TwilioStatistics {
  total_messages: number;
  total_calls: number;
  last_sync_messages?: string;
  last_sync_calls?: string;
  is_active: boolean;
}

interface TwilioExportRequest {
  date_from?: string;
  date_to?: string;
  export_format?: string;
}

interface TwilioExportResponse {
  success: boolean;
  message?: string;
  download_url?: string;
  filename?: string;
}


export const twilioApi = createApi({
  reducerPath: 'twilioApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl('/api/v1/twilio'),
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      console.log('Twilio API - Token:', token ? 'Present' : 'Missing');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['TwilioConfiguration', 'TwilioMessage', 'TwilioCall'],
  endpoints: (builder) => ({
    // Configuration endpoints
    getTwilioConfigurations: builder.query<TwilioConfiguration[], void>({
      query: () => '/configurations',
      providesTags: ['TwilioConfiguration'],
    }),
    getTwilioConfiguration: builder.query<TwilioConfiguration, number>({
      query: (configId) => `/configurations/${configId}`,
      providesTags: ['TwilioConfiguration'],
    }),
    createTwilioConfiguration: builder.mutation<TwilioConfiguration, TwilioConfigurationCreate>({
      query: (configData) => ({
        url: '/configurations',
        method: 'POST',
        body: configData,
      }),
      invalidatesTags: ['TwilioConfiguration'],
    }),
    updateTwilioConfiguration: builder.mutation<TwilioConfiguration, { id: number; data: TwilioConfigurationUpdate }>({
      query: ({ id, data }) => ({
        url: `/configurations/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['TwilioConfiguration'],
    }),
    deleteTwilioConfiguration: builder.mutation<void, number>({
      query: (configId) => ({
        url: `/configurations/${configId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TwilioConfiguration'],
    }),

    // Data endpoints
    getTwilioMessages: builder.query<TwilioMessage[], { configId: number; limit?: number; offset?: number }>({
      query: ({ configId, limit = 100, offset = 0 }) => 
        `/configurations/${configId}/messages?limit=${limit}&offset=${offset}`,
      providesTags: ['TwilioMessage'],
    }),
    getTwilioCalls: builder.query<TwilioCall[], { configId: number; limit?: number; offset?: number }>({
      query: ({ configId, limit = 100, offset = 0 }) => 
        `/configurations/${configId}/calls?limit=${limit}&offset=${offset}`,
      providesTags: ['TwilioCall'],
    }),

    // Sync and statistics
    syncTwilioData: builder.mutation<TwilioSyncResponse, { configId: number; forceFullSync?: boolean }>({
      query: ({ configId, forceFullSync = false }) => ({
        url: `/configurations/${configId}/sync`,
        method: 'POST',
        body: { force_full_sync: forceFullSync },
      }),
      invalidatesTags: ['TwilioMessage', 'TwilioCall', 'TwilioConfiguration'],
    }),
    getTwilioStatistics: builder.query<TwilioStatistics, number>({
      query: (configId) => `/configurations/${configId}/statistics`,
      providesTags: ['TwilioConfiguration'],
    }),
    exportTwilioData: builder.mutation<TwilioExportResponse, { configId: number; data: TwilioExportRequest }>({
      query: ({ configId, data }) => ({
        url: `/configurations/${configId}/export`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetTwilioConfigurationsQuery,
  useGetTwilioConfigurationQuery,
  useCreateTwilioConfigurationMutation,
  useUpdateTwilioConfigurationMutation,
  useDeleteTwilioConfigurationMutation,
  useGetTwilioMessagesQuery,
  useGetTwilioCallsQuery,
  useSyncTwilioDataMutation,
  useGetTwilioStatisticsQuery,
  useExportTwilioDataMutation,
} = twilioApi;
